// What a client may tell the server about itself (#459), and nothing else.
//
// Error Reporting covers the functions, not the clients (docs/operations.md
// §5), so a thrown error, a write the rules refused, a queue that stopped
// draining and a sign-in that failed are sent through `reportClientEvent`, the
// one callable that carries them. The allow-list is what both ends read — the
// client to decide what to send, the function (through the `functions/domain/`
// mirror) to decide what to log — so the two cannot disagree about a field.
//
// **The rules are `core/src/client_events.rs`** (#496), shared with the
// Android and iOS apps: the allow-list and each field's bound, the
// address redaction, the version check, what makes two events the same, the
// run's cap and a stalled queue are one implementation that the web, the
// function and both phones ask; `core/src/client_events_vectors.json` holds
// the recorded answers. This file is the adapter: a loose JavaScript payload
// coerced to the core's flat record (a string field is `String(v)`, a count
// `Number(v)`, a missing one absent) and the answer turned back into a plain
// object with only the fields that survived.
//
// **Not for counting.** The client is offline-first: an event may arrive an
// hour late or never. Product numbers are the server-side fold
// (functions/health/); these are what *only* the client can see.
//
// **No personal data.** Every field is bounded and typed, a message has any
// email address in it replaced, and `client.sign_in_failed` carries the kind
// of failure from `authErrors.js`, never the address that was typed. The uid
// is added by the server from the verified token, never taken from the body.
//
// Pure, no Firebase: this file is copied into functions/domain/, so every
// relative import carries its `.js`.
import { coreSync } from './core.js';

// The two tables stay as literals, for callers that read them at module scope
// (the sign-in page, SyncContext); `test/core/clientEvents.wasm.test.js` pins
// them to the core's.
export const CLIENT_EVENTS = Object.freeze({
  error: 'client.error',
  writeRefused: 'client.write_refused',
  syncStalled: 'client.sync_stalled',
  signInFailed: 'client.sign_in_failed',
});

// Which events may be sent with nobody signed in: a sign-in that failed, and
// an error before auth has resolved. The other two need the studio's uid.
export const ANONYMOUS_EVENTS = Object.freeze([CLIENT_EVENTS.error, CLIENT_EVENTS.signInFailed]);

const TEXT_FIELDS = ['message', 'stackHash', 'route', 'source', 'subject', 'code', 'reason', 'kind', 'mode', 'platform'];
const COUNT_FIELDS = ['depth', 'oldestMs'];

/** A loose payload as the core's flat record. */
function toPayload(given) {
  const from = given && typeof given === 'object' && !Array.isArray(given) ? given : {};
  const out = {};
  for (const k of TEXT_FIELDS) if (from[k] != null) out[k] = String(from[k]);
  for (const k of COUNT_FIELDS) if (from[k] != null) out[k] = Number(from[k]);
  return out;
}

/** The core's record back as a plain object with only the fields it kept. */
function fromPayload(p) {
  const out = {};
  for (const k of [...TEXT_FIELDS, ...COUNT_FIELDS]) if (p?.[k] != null) out[k] = p[k];
  return out;
}

const versionArg = (v) => (typeof v === 'string' ? v : undefined);

/** The run's cap, the stall's threshold and check interval, and the lists. */
export function clientEventPolicy() {
  const p = coreSync().clientEventPolicy();
  return {
    sessionCap: p.sessionCap,
    stallAfterMs: p.stallAfterMs,
    stallCheckMs: p.stallCheckMs,
    events: [...p.events],
    anonymousEvents: [...p.anonymousEvents],
    platforms: [...p.platforms],
  };
}

export const isClientEvent = (name) => Object.values(CLIENT_EVENTS).includes(name);

/**
 * The event as it may be logged, or why not. Unknown fields are dropped, known
 * ones typed and bounded; a name off the list is refused rather than cleaned.
 */
export function cleanClientEvent({ name, payload, appVersion } = {}) {
  const c = coreSync().clientEventClean(String(name ?? ''), toPayload(payload), versionArg(appVersion));
  if (!c.ok) return { ok: false, reason: c.reason };
  return { ok: true, name: c.name, fields: fromPayload(c.payload), appVersion: c.appVersion };
}

/**
 * What a client sends, given the keys this run has sent and how many — the
 * cleaned payload and its key — or why nothing: `unknown_event`, `repeat` or
 * `over_cap`. The caller records the key and counts one, then sends.
 */
export function prepareClientEvent({ name, payload, appVersion, seen = [], sent = 0 } = {}) {
  const p = coreSync().clientEventPrepare(
    String(name ?? ''), toPayload(payload), versionArg(appVersion), [...seen].map(String), sent,
  );
  if (!p.send) return { send: false, reason: p.reason };
  return { send: true, key: p.key, fields: fromPayload(p.payload), appVersion: p.appVersion };
}

/** FNV-1a, 32 bits, hex, over UTF-16 units: a stable short name for a stack, not a secret. */
export const stackHash = (stack) => coreSync().clientStackHash(String(stack ?? ''));

/**
 * What makes two events the same event, for the run's de-duplication: an
 * error by where it was thrown, a refusal by what and why, a stall once, a
 * failed sign-in by its kind.
 */
export const clientEventKey = (name, fields = {}) => coreSync().clientEventKey(String(name ?? ''), toPayload(fields));

/**
 * `{ depth, oldestMs }` for this studio's owed batches when the oldest has
 * been owed longer than the core's threshold, else null. `records` is the
 * queue's `records()`: a dead batch is not owed, and a batch is this studio's
 * when one of its writes is under `users/{uid}/`. A record with no enqueue
 * time is counted but not aged (#496).
 */
export function clientSyncStall(records, { uid, now }) {
  const batches = (records || []).filter(Boolean).map((r) => ({
    state: String(r.state ?? ''),
    enqueuedAtMs: r.enqueuedAt == null ? undefined : Number(r.enqueuedAt),
    paths: Array.isArray(r.ops) ? r.ops.map((o) => String(o?.path ?? '')) : [],
  }));
  const s = coreSync().clientSyncStall(batches, String(uid ?? ''), Number(now));
  return s ? { depth: s.depth, oldestMs: s.oldestMs } : null;
}

// ── firebase-functions' own refusal, said (#493) ───────────────────────────
//
// With App Check enforced, firebase-functions refuses a callable with no
// valid App Check token before our handler runs, as code `unauthenticated`
// and the bare message "Unauthenticated" — the same pair it gives an ID token
// that does not verify. What that means to a teacher is the core's
// (`callable_refusal_message` in core/src/client_events.rs, one table for the
// web and both phones); our handlers' own `unauthenticated` sentences are not
// that word and pass through.

/**
 * The sentence for a callable's failure when it is firebase-functions' own
 * refusal, or null. `signedIn` is whether the call carried a teacher's ID
 * token, which every callable the web makes does.
 */
export function unverifiedRefusal(err, { signedIn = true } = {}) {
  if (err?.unverified === true && typeof err.message === 'string') return err.message;
  const said = coreSync().callableRefusalMessage(
    String(err?.code ?? ''),
    typeof err?.message === 'string' ? err.message : undefined,
    'web',
    Boolean(signedIn),
  );
  return said ?? null;
}

/** The sentence itself, for a screen that kept only the kind of failure. */
export const unverifiedMessage = ({ signedIn = true } = {}) => coreSync().unverifiedMessage('web', Boolean(signedIn));

/**
 * The error a callable rejected with, or — when it was firebase-functions'
 * own refusal — one that says the sentence, keeps the code and details, and
 * is marked `unverified` so a later mapper does not replace it again.
 */
export function sayUnverified(err, opts) {
  if (err?.unverified === true) return err;
  const sentence = unverifiedRefusal(err, opts);
  if (!sentence) return err;
  const said = new Error(sentence, { cause: err });
  said.code = err.code;
  said.details = err.details;
  said.unverified = true;
  return said;
}
